import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { userDtos } from '@vectronic/openApi-customer';
import { ConfirmationDialogComponent } from '@vectronic/ui';
import { DialogService, MemberType, TableCollapseService } from '@vectronic/util';
import { ChipsCellComponent } from '../chips-cell/chips-cell.component';
import { ChipsCellItem, ChipsCellOption } from '../chips-cell/chips-cell.models';

@Component({
  selector: 'table-group-chips-cell',
  template: `
    <table-chips-cell
      [items]="items()"
      [options]="options()"
      [showMore]="showMore()"
      [isCompactModeActivated]="isCompactModeActivated()"
      [canExpand]="canExpand()"
      [overflowTooltip]="overflowTooltip()"
      [collapsedMaxLength]="15"
      [expandedMaxLength]="40"
      placeholder="Add group"
      ariaLabel="Groups"
      (add)="onAdd($event)"
      (selected)="onSelected($event)"
      (remove)="onRemove($event)"
      (toggleShowMore)="onToggleShowMore($event)"
    ></table-chips-cell>
  `,
  imports: [ChipsCellComponent],
})
export class GroupChipsCellComponent {
  member = input<MemberType>();
  groupsWithMember = input<userDtos.Group[]>();
  groups = input<userDtos.Group[]>();
  isCompactModeActivated = input<boolean>(false);

  addGroup = output<userDtos.Group>();
  updateGroup = output<userDtos.Group>();
  deleteGroup = output<userDtos.Group>();

  private dialogService = inject(DialogService);
  private tableCollapseService = inject(TableCollapseService);

  private selectionChanged = toSignal(this.tableCollapseService.onSelectionChanged$, {
    initialValue: undefined as number | string | undefined,
  });

  isReader = computed(() => this.member().role === 'READER');
  canExpand = computed(() => !this.isReader());

  showMore = computed(() => {
    this.selectionChanged();
    return this.tableCollapseService.isGroupSelectedMemberId(this.member()?.id);
  });

  groupsWhereYouCanAddTheMember = computed(() => {
    const groupIds = this.groupsWithMember()?.map(({ id }) => id);
    return this.groups()?.filter(({ id, role }) => !groupIds?.includes(id) && role !== 'READER');
  });

  items = computed<ChipsCellItem<userDtos.Group>[]>(() =>
    (this.groupsWithMember() ?? []).map((group) => ({
      id: group.id,
      label: group.name,
      tooltip: group.name,
      data: group,
    })),
  );

  options = computed<ChipsCellOption<userDtos.Group>[]>(() =>
    (this.groupsWhereYouCanAddTheMember() ?? []).map((group) => ({
      value: group.name,
      label: group.name,
      data: group,
    })),
  );

  overflowTooltip = computed(() =>
    (this.groupsWithMember() ?? []).map(({ name }) => name).join(', '),
  );

  onAdd(value: string) {
    this.addGroup.emit({
      description: '',
      id: null,
      label: '',
      name: value,
      membersIds: [this.member().id],
    });
  }

  onSelected(group: userDtos.Group) {
    this.updateGroup.emit({
      ...group,
      membersIds: [...group.membersIds, this.member().id],
    });
  }

  onRemove(group: userDtos.Group) {
    this.dialogService.open(ConfirmationDialogComponent, {
      afterConfirm: ({ toggle }) => this.onRemoveMemberFromGroup(group, toggle),
      data: {
        title: `Delete member from group`,
        message: `Are you sure you want to remove ${this.member().name} from the group?`,
        toggleText: `Remove permission to ${this.member().name} for any user with a share to this group`,
      },
    });
  }

  onToggleShowMore(expand: boolean) {
    this.tableCollapseService.setGroupSelectedMemberId(expand ? this.member().id : undefined);
  }

  private onRemoveMemberFromGroup(selectedGroup: userDtos.Group, removeAccess = false) {
    const newMembers = selectedGroup.membersIds.filter((memberId) => memberId !== this.member().id);
    if (newMembers?.length) {
      const newGroup = { ...selectedGroup, membersIds: newMembers, removeAccess };
      this.updateGroup.emit(newGroup);
    } else {
      this.deleteGroup.emit(selectedGroup);
    }
  }
}
